import { compact } from "lodash";
import { useMemo, useState } from "react";
import {
  AssessmentGroupAnonymity,
  AssessmentGroupDelivery,
  AssessmentGroupListFragmentFragment,
  AssessmentGroupProviders,
  AssessmentTemplateComplianceRequirement,
  AssessmentType,
  Maybe,
  SaveComplianceProgramMutationVariables,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar, isAdminVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import { ComboboxGenericOption } from "@components/combobox/generic-combobox";
import FormFields from "@components/form/form-fields";
import DeprecatedSelect from "@components/select/select";
import ToggleSwitch from "@components/toggle-switch/toggle-switch";
import InfoTooltip from "@components/tooltip/info-tooltip";
import WarningTooltip from "@components/tooltip/warning-tooltip";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon-helpers";
import {
  assessmentGroupAnonymityOptions,
  assessmentGroupDeliveryOptions,
  assessmentGroupProviderOptions,
} from "@helpers/constants";
import { assertNonNull } from "@helpers/helpers";

import AssessmentGroupSidebar from "../assessment-group-sidebar/assessment-group-sidebar";
import {
  AssessmentTemplateExtraSettings,
  ComplianceProgramStep,
} from "../types";
import ComplianceProgramFormStep from "./compliance-program-form-step";
import CoreValueAssessmentSettingsModal from "./core-value-assessment-settings-modal";
import GoalAssessmentSettingsModal from "./goal-assessment-settings-modal";
import QuestionSetSelector from "./question-set-selector";
import RoleBasedAssessmentSettingsModal from "./role-based-assessment-settings-modal";

const PeerAssessmentStep = ({
  proposedComplianceProgram,
  canUpdate,
  hideSettings,
  complianceProgramSteps,
  onUpdateComplianceProgramSteps,
  onUpdateAssessmentTemplateSettings,
  onUpdateAssessmentTemplate,
  availableAssessmentGroups,
  onRemoveAssessmentTemplate,
  onAssessmentGroupCreated,
  onAssessmentGroupArchived,
}: {
  proposedComplianceProgram: SaveComplianceProgramMutationVariables;
  canUpdate: boolean;
  hideSettings?: boolean;
  complianceProgramSteps: ComplianceProgramStep[];
  onUpdateComplianceProgramSteps: (steps: ComplianceProgramStep[]) => void;
  onUpdateAssessmentTemplateSettings: (
    assessmentType: AssessmentType,
  ) => (settings: AssessmentTemplateExtraSettings) => void;
  onUpdateAssessmentTemplate: (
    assessmentType: AssessmentType,
  ) => (opt: ComboboxGenericOption<number> | null) => void;
  availableAssessmentGroups: AssessmentGroupListFragmentFragment[];
  onRemoveAssessmentTemplate: (assessmentType: AssessmentType) => void;
  onAssessmentGroupCreated: (
    newAssessmentGroup: AssessmentGroupListFragmentFragment,
  ) => void;
  onAssessmentGroupArchived: (
    assessmentType: AssessmentType,
    assessmentGroupId: number,
  ) => void;
}) => {
  const label = useLabel();
  const organization = currentOrganizationVar();
  const isAdmin = isAdminVar();
  const hasGoalBasedAssessments = organization.featureFlags.goals;

  const [isRoleBasedAssessmentModalOpen, setIsRoleBasedAssessmentModalOpen] =
    useState(false);
  const [isGoalBasedAssessmentModalOpen, setIsGoalBasedAssessmentModalOpen] =
    useState(false);
  const [
    isCoreValueBasedAssessmentModalOpen,
    setIsCoreValueBasedAssessmentModalOpen,
  ] = useState(false);
  const [isAssessmentGroupSidebarOpen, setIsAssessmentGroupSidebarOpen] =
    useState(false);
  const [assessmentGroupToDuplicate, setAssessmentGroupToDuplicate] =
    useState<AssessmentGroupListFragmentFragment | null>(null);

  const availableAssessmentGroupSelectOptions = useMemo(() => {
    return availableAssessmentGroups
      .filter(({ assessmentType }) => assessmentType === AssessmentType.Peer)
      .filter(
        (assessmentGroup) =>
          !assessmentGroup.isOnlySelfAssessment &&
          !assessmentGroup.hasNpsQuestions,
      )
      .map(({ id, title }) => ({
        value: id,
        label: title,
      }));
  }, [availableAssessmentGroups]);

  const selectedAssessmentTemplates = useMemo(
    () =>
      proposedComplianceProgram.assessmentTemplates
        ? compact(
            proposedComplianceProgram.assessmentTemplates as Maybe<AssessmentTemplateComplianceRequirement>[],
          )
        : [],
    [proposedComplianceProgram.assessmentTemplates],
  );

  const selectedPeerAssessmentTemplate = useMemo(() => {
    return selectedAssessmentTemplates.find(
      (template) => template.assessmentType === AssessmentType.Peer,
    );
  }, [selectedAssessmentTemplates]);

  const selectedAssessmentGroup = useMemo(() => {
    return selectedPeerAssessmentTemplate &&
      selectedPeerAssessmentTemplate.questionSetIds.length > 0
      ? (availableAssessmentGroups.find(
          ({ id }) => id === selectedPeerAssessmentTemplate.questionSetIds[0],
        ) ?? null)
      : null;
  }, [availableAssessmentGroups, selectedPeerAssessmentTemplate]);

  const peerDeliveryOptions = useMemo(
    () => assessmentGroupDeliveryOptions(label, AssessmentType.Peer),
    [label],
  );

  return (
    <>
      {isAssessmentGroupSidebarOpen && (
        <AssessmentGroupSidebar
          assessmentType={AssessmentType.Peer}
          assessmentGroupId={selectedAssessmentGroup?.id}
          assessmentGroupToDuplicateId={assessmentGroupToDuplicate?.id}
          onClose={() => {
            setAssessmentGroupToDuplicate(null);
            setIsAssessmentGroupSidebarOpen(false);
          }}
          onAssessmentGroupCreated={(assessmentGroup) => {
            onAssessmentGroupCreated(assessmentGroup);
            onUpdateAssessmentTemplate(AssessmentType.Peer)({
              value: assessmentGroup.id,
              label: assessmentGroup.title,
            });
          }}
          onAssessmentGroupArchived={(assessmentGroupId) =>
            onAssessmentGroupArchived(AssessmentType.Peer, assessmentGroupId)
          }
        />
      )}
      {isRoleBasedAssessmentModalOpen && selectedPeerAssessmentTemplate && (
        <RoleBasedAssessmentSettingsModal
          assessmentType={AssessmentType.Peer}
          onClose={() => setIsRoleBasedAssessmentModalOpen(false)}
          canUpdate={canUpdate}
          selectedPerformanceAssessmentGroup={selectedPeerAssessmentTemplate}
          onChangeAssessmentTemplateSettings={onUpdateAssessmentTemplateSettings(
            AssessmentType.Peer,
          )}
        />
      )}
      {isGoalBasedAssessmentModalOpen && selectedPeerAssessmentTemplate && (
        <GoalAssessmentSettingsModal
          assessmentType={AssessmentType.Peer}
          onClose={() => setIsGoalBasedAssessmentModalOpen(false)}
          canUpdate={canUpdate}
          selectedPerformanceAssessmentGroup={selectedPeerAssessmentTemplate}
          onChangeAssessmentTemplateSettings={onUpdateAssessmentTemplateSettings(
            AssessmentType.Peer,
          )}
        />
      )}
      {isCoreValueBasedAssessmentModalOpen &&
        selectedPeerAssessmentTemplate && (
          <CoreValueAssessmentSettingsModal
            assessmentType={AssessmentType.Peer}
            onClose={() => setIsCoreValueBasedAssessmentModalOpen(false)}
            canUpdate={canUpdate}
            selectedPerformanceAssessmentGroup={selectedPeerAssessmentTemplate}
            onChangeAssessmentTemplateSettings={onUpdateAssessmentTemplateSettings(
              AssessmentType.Peer,
            )}
          />
        )}
      <ComplianceProgramFormStep
        canDelete={canUpdate && !hideSettings}
        title={`Peer ${label("review", { capitalize: true })}`}
        onDelete={() => {
          onRemoveAssessmentTemplate(AssessmentType.Peer);
          onUpdateComplianceProgramSteps(
            complianceProgramSteps.filter(
              (s) => s !== ComplianceProgramStep.PEER_REVIEW,
            ),
          );
        }}
      >
        <div className="px-6 pb-6 flex flex-col gap-3">
          <div className="flex items-center gap-3 p-4 bg-white border rounded">
            <QuestionSetSelector
              assessmentType={AssessmentType.Peer}
              availableAssessmentGroupSelectOptions={
                availableAssessmentGroupSelectOptions
              }
              canUpdate={canUpdate}
              onUpdateAssessmentTemplate={onUpdateAssessmentTemplate}
              onDuplicateAssessmentGroup={setAssessmentGroupToDuplicate}
              onOpenAssessmentGroupSidebar={() =>
                setIsAssessmentGroupSidebarOpen(true)
              }
              selectedAssessmentGroup={selectedAssessmentGroup}
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-white border rounded">
            <div className="flex items-center gap-2">
              <div className="text-sm">{`Role ${label("review", {
                capitalize: true,
              })}`}</div>
              <InfoTooltip text="Use this if you would like to assess an individual based on their job responsibilities and competencies." />
            </div>

            {!organization.hasCareerFramework ? (
              <WarningTooltip
                size={6}
                text={`There are no Career Tracks defined for your ${label(
                  "organization",
                )}.${
                  isAdmin
                    ? ` Go to ${label("organization", {
                        capitalize: true,
                      })} settings to define them.`
                    : ""
                }`}
              />
            ) : (
              <div className="flex items-center gap-4">
                <Button
                  className="mb-1"
                  disabled={
                    !selectedPeerAssessmentTemplate ||
                    !selectedPeerAssessmentTemplate?.enableRoleBasedAssessments
                  }
                  icon={TopicflowIconMap.edit}
                  theme={buttonTheme.iconGray}
                  onClick={() => setIsRoleBasedAssessmentModalOpen(true)}
                />
                <ToggleSwitch
                  disabled={!canUpdate || !selectedPeerAssessmentTemplate}
                  checked={
                    !!selectedPeerAssessmentTemplate?.enableRoleBasedAssessments
                  }
                  onChange={(enabled) =>
                    onUpdateAssessmentTemplateSettings(AssessmentType.Peer)({
                      enableRoleBasedAssessments: enabled,
                      roleBasedAssessmentsAssessNextRole: false,
                      roleBasedAssessmentsSelfAssessNextRole: false,
                    })
                  }
                />
              </div>
            )}
          </div>

          {hasGoalBasedAssessments && (
            <div className="flex items-center justify-between p-4 bg-white border rounded">
              <div className="flex items-center gap-2">
                <div className="text-sm">{`${label("goal", {
                  capitalize: true,
                })} ${label("review", {
                  capitalize: true,
                })}`}</div>
                <InfoTooltip
                  text={`Use this if you would like to assess an individual's progress towards their ${label(
                    "goal",
                    {
                      pluralize: true,
                    },
                  )}.`}
                />
              </div>

              <div className="flex items-center gap-4">
                <Button
                  className="mb-1"
                  disabled={
                    !selectedPeerAssessmentTemplate ||
                    !selectedPeerAssessmentTemplate?.enableGoalAssessments
                  }
                  icon={TopicflowIconMap.edit}
                  theme={buttonTheme.iconGray}
                  onClick={() => setIsGoalBasedAssessmentModalOpen(true)}
                />
                <ToggleSwitch
                  disabled={!canUpdate || !selectedPeerAssessmentTemplate}
                  checked={
                    !!selectedPeerAssessmentTemplate?.enableGoalAssessments
                  }
                  onChange={(enabled) =>
                    onUpdateAssessmentTemplateSettings(AssessmentType.Peer)({
                      enableGoalAssessments: enabled,
                    })
                  }
                />
              </div>
            </div>
          )}

          <div className="flex items-center justify-between p-4 bg-white border rounded">
            <div className="flex items-center gap-2">
              <div className="text-sm">{`Values ${label("review", {
                capitalize: true,
              })}`}</div>
              <InfoTooltip text="Use this if you would like to assess an individual's alignment with your Core Values" />
            </div>

            {organization.coreValues?.edges.length === 0 ? (
              <WarningTooltip
                size={6}
                text={`There are no Core Values defined for your ${label(
                  "organization",
                )}.${
                  isAdmin
                    ? ` Go to ${label("organization", {
                        capitalize: true,
                      })} settings to define them.`
                    : ""
                }`}
              />
            ) : (
              <div className="flex items-center gap-4">
                <Button
                  className="mb-1"
                  disabled={
                    !selectedPeerAssessmentTemplate ||
                    !selectedPeerAssessmentTemplate?.enableCoreValueAssessments
                  }
                  icon={TopicflowIconMap.edit}
                  theme={buttonTheme.iconGray}
                  onClick={() => setIsCoreValueBasedAssessmentModalOpen(true)}
                />
                <ToggleSwitch
                  disabled={!canUpdate || !selectedPeerAssessmentTemplate}
                  checked={
                    !!selectedPeerAssessmentTemplate?.enableCoreValueAssessments
                  }
                  onChange={(enabled) =>
                    onUpdateAssessmentTemplateSettings(AssessmentType.Peer)({
                      enableCoreValueAssessments: enabled,
                    })
                  }
                />
              </div>
            )}
          </div>
        </div>

        {!hideSettings && (
          <div className="p-6 border-t">
            <div className="text-sm text-gray-600 font-medium mb-4">
              Settings
            </div>
            <FormFields
              fields={[
                {
                  title: "Providers",
                  render: () =>
                    selectedPeerAssessmentTemplate ? (
                      <DeprecatedSelect<AssessmentGroupProviders>
                        disabled={!canUpdate}
                        value={assertNonNull(
                          selectedPeerAssessmentTemplate.providers,
                        )}
                        options={assessmentGroupProviderOptions(
                          AssessmentType.Peer,
                        )}
                        onChange={(opt) =>
                          onUpdateAssessmentTemplateSettings(
                            AssessmentType.Peer,
                          )({
                            providers: opt.value,
                          })
                        }
                        aria-label="Assessment providers select"
                      />
                    ) : (
                      <div className="text-sm h-10 flex items-center">-</div>
                    ),
                },
                {
                  title: "Anonymity",
                  render: () =>
                    selectedPeerAssessmentTemplate ? (
                      <DeprecatedSelect<AssessmentGroupAnonymity>
                        disabled={!canUpdate}
                        value={assertNonNull(
                          selectedPeerAssessmentTemplate.anonymity,
                        )}
                        options={assessmentGroupAnonymityOptions(
                          AssessmentType.Peer,
                        )}
                        onChange={(opt) =>
                          onUpdateAssessmentTemplateSettings(
                            AssessmentType.Peer,
                          )({
                            anonymity: opt.value,
                          })
                        }
                      />
                    ) : (
                      <div className="text-sm h-10 flex items-center">-</div>
                    ),
                },
                {
                  title: "Delivery",
                  render: () =>
                    selectedPeerAssessmentTemplate ? (
                      <DeprecatedSelect<AssessmentGroupDelivery>
                        disabled={!canUpdate}
                        value={assertNonNull(
                          selectedPeerAssessmentTemplate.delivery,
                        )}
                        options={peerDeliveryOptions}
                        onChange={(opt) =>
                          onUpdateAssessmentTemplateSettings(
                            AssessmentType.Peer,
                          )({
                            delivery: opt.value,
                          })
                        }
                      />
                    ) : (
                      <div className="text-sm h-10 flex items-center">-</div>
                    ),
                },
              ]}
            />
          </div>
        )}
      </ComplianceProgramFormStep>
    </>
  );
};
export default PeerAssessmentStep;
